class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    if (!this.input) return;

    this.input.form?.addEventListener('reset', this.onFormReset.bind(this));
    this.input.addEventListener('input', debounce(() => this.onChange(), 120));
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    this.resetButton.classList.toggle('hidden', this.input.value.length === 0);
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"]');
  }

  onFormReset(event) {
    event.preventDefault();
    if (!this.shouldResetForm()) return;
    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
  }
}

customElements.define('search-form', SearchForm);

class GearfullHeaderSearch {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('form[role="search"]');
    this.input = root.querySelector('input[type="search"]');
    this.cache = new Map();
    this.controller = null;
    this.items = [];
    this.active = -1;
    if (!this.form || !this.input) return;

    this.addStyles();
    this.addPanel();
    this.bind();
  }

  addStyles() {
    if (document.getElementById('GearfullHeaderSearchCSS')) return;
    const style = document.createElement('style');
    style.id = 'GearfullHeaderSearchCSS';
    style.textContent = `.gearfull-inline-search{position:relative}.gearfull-search-results{position:absolute;top:calc(100% + .8rem);left:0;right:0;display:none;max-height:min(68vh,62rem);overflow:auto;background:rgb(var(--color-background));border:.1rem solid rgba(var(--color-foreground),.14);border-radius:1.8rem;box-shadow:0 1.8rem 4.8rem rgba(0,0,0,.16);z-index:80}.gearfull-search-results.is-open{display:block}.gearfull-search-results__status{padding:1.6rem 1.8rem;color:rgba(var(--color-foreground),.62);font-size:1.3rem}.gearfull-search-results__heading{margin:0;padding:1.5rem 1.8rem .7rem;color:rgba(var(--color-foreground),.56);font-size:1.05rem;font-weight:800;letter-spacing:.09em;text-transform:uppercase}.gearfull-search-results__list{list-style:none;margin:0;padding:0}.gearfull-search-results__link{display:grid;grid-template-columns:5.6rem minmax(0,1fr);align-items:center;gap:1.2rem;min-height:7rem;padding:.7rem 1.8rem;color:rgb(var(--color-foreground));text-decoration:none;outline:0}.gearfull-search-results__link:hover,.gearfull-search-results__link[aria-selected=true],.gearfull-search-results__all:hover,.gearfull-search-results__all[aria-selected=true]{background:rgba(var(--color-foreground),.055)}.gearfull-search-results__image{width:5.6rem;height:5.6rem;display:block;object-fit:contain;border-radius:.8rem;background:rgba(var(--color-foreground),.035)}.gearfull-search-results__image--empty{border:.1rem solid rgba(var(--color-foreground),.08)}.gearfull-search-results__copy{min-width:0}.gearfull-search-results__title,.gearfull-search-results__meta{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.gearfull-search-results__title{font-size:1.35rem;font-weight:700;line-height:1.3}.gearfull-search-results__meta{margin-top:.25rem;color:rgba(var(--color-foreground),.58);font-size:1.15rem}.gearfull-search-results__all{display:flex;align-items:center;justify-content:space-between;gap:1rem;min-height:5.2rem;padding:1.2rem 1.8rem;border-top:.1rem solid rgba(var(--color-foreground),.1);color:rgb(var(--color-foreground));font-size:1.3rem;font-weight:750;text-decoration:none;outline:0}@media(max-width:989px){.gearfull-search-results{display:none!important}}`;
    document.head.appendChild(style);
  }

  addPanel() {
    this.panel = document.createElement('div');
    this.panel.id = this.root.classList.contains('gearfull-mobile-search') ? 'GearfullMobileSearchResults' : 'GearfullHeaderSearchResults';
    this.panel.className = 'gearfull-search-results';
    this.panel.setAttribute('role', 'listbox');
    this.root.appendChild(this.panel);

    this.status = document.createElement('span');
    this.status.className = 'visually-hidden';
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');
    this.root.appendChild(this.status);

    Object.entries({
      role: 'combobox',
      'aria-autocomplete': 'list',
      'aria-controls': this.panel.id,
      'aria-expanded': 'false',
      autocomplete: 'off',
    }).forEach(([name, value]) => this.input.setAttribute(name, value));
  }

  bind() {
    this.input.addEventListener('input', debounce(() => this.search(), 120));
    this.input.addEventListener('focus', () => this.search(true));
    this.input.addEventListener('keydown', (event) => this.keydown(event));
    this.form.addEventListener('submit', (event) => {
      if (this.active < 0) return;
      event.preventDefault();
      this.items[this.active]?.click();
    });
    document.addEventListener('pointerdown', (event) => {
      if (!this.root.contains(event.target)) this.close();
    });
  }

  search(focusOnly = false) {
    const term = this.input.value.trim();
    this.active = -1;
    if (term.length < 2) return this.close();

    const cached = this.cache.get(term.toLowerCase());
    if (cached) return this.render(cached, term);
    if (!focusOnly) this.fetch(term);
  }

  async fetch(term) {
    this.controller?.abort();
    this.controller = new AbortController();
    this.message('Searching…');

    const base = window.routes?.predictive_search_url || `${window.Shopify?.routes?.root || '/'}search/suggest`;
    const url = new URL(base.endsWith('.json') ? base : `${base}.json`, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '7');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,vendor,variants.title,variants.sku,variants.barcode');

    try {
      const response = await fetch(url, { signal: this.controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Predictive search failed (${response.status})`);
      const data = await response.json();
      if (this.input.value.trim() !== term) return;
      this.cache.set(term.toLowerCase(), data);
      this.render(data, term);
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
      if (error.name !== 'AbortError') this.close();
    }
  }

  render(data, term) {
    const products = (data?.resources?.results?.products || []).slice(0, 7);
    this.panel.replaceChildren();

    if (products.length) {
      const heading = document.createElement('p');
      heading.className = 'gearfull-search-results__heading';
      heading.textContent = 'Products';
      this.panel.appendChild(heading);

      const list = document.createElement('ul');
      list.className = 'gearfull-search-results__list';
      products.forEach((product) => {
        const row = document.createElement('li');
        row.appendChild(this.productLink(product));
        list.appendChild(row);
      });
      this.panel.appendChild(list);
    }

    const all = document.createElement('a');
    all.className = 'gearfull-search-results__all';
    all.href = this.fullSearchUrl(term);
    all.setAttribute('role', 'option');
    all.setAttribute('aria-selected', 'false');
    const label = document.createElement('span');
    label.textContent = products.length ? `View all results for “${term}”` : `Search for “${term}”`;
    const arrow = document.createElement('span');
    arrow.textContent = '→';
    arrow.setAttribute('aria-hidden', 'true');
    all.append(label, arrow);
    this.panel.appendChild(all);

    this.items = [...this.panel.querySelectorAll('[role="option"]')];
    this.open();
    this.status.textContent = products.length ? `${products.length} product suggestions available` : 'No direct product matches';
  }

  productLink(product) {
    const link = document.createElement('a');
    link.className = 'gearfull-search-results__link';
    link.href = product.url;
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');

    if (product.featured_image?.url) {
      const image = document.createElement('img');
      image.className = 'gearfull-search-results__image';
      image.src = this.imageUrl(product.featured_image.url);
      image.alt = product.featured_image.alt || '';
      image.width = 56;
      image.height = 56;
      link.appendChild(image);
    } else {
      const empty = document.createElement('span');
      empty.className = 'gearfull-search-results__image gearfull-search-results__image--empty';
      empty.setAttribute('aria-hidden', 'true');
      link.appendChild(empty);
    }

    const copy = document.createElement('span');
    copy.className = 'gearfull-search-results__copy';
    const title = document.createElement('span');
    title.className = 'gearfull-search-results__title';
    title.textContent = product.title;
    copy.appendChild(title);

    const metaText = [product.vendor, product.type].filter(Boolean).join(' · ');
    if (metaText) {
      const meta = document.createElement('span');
      meta.className = 'gearfull-search-results__meta';
      meta.textContent = metaText;
      copy.appendChild(meta);
    }

    link.appendChild(copy);
    return link;
  }

  fullSearchUrl(term) {
    const url = new URL(this.form.action, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    return url;
  }

  imageUrl(source) {
    try {
      const url = new URL(source, location.origin);
      url.searchParams.set('width', '112');
      return url;
    } catch (_) {
      return source;
    }
  }

  message(text) {
    const status = document.createElement('div');
    status.className = 'gearfull-search-results__status';
    status.textContent = text;
    this.panel.replaceChildren(status);
    this.items = [];
    this.open();
  }

  keydown(event) {
    if (!this.panel.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      return this.close();
    }
    if (!this.items.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
    if (event.key === 'Enter' && this.active >= 0) {
      event.preventDefault();
      return this.items[this.active].click();
    }
    if (event.key === 'Enter') return;

    event.preventDefault();
    const next = event.key === 'ArrowDown'
      ? (this.active + 1) % this.items.length
      : (this.active <= 0 ? this.items.length : this.active) - 1;
    this.select(next);
  }

  select(index) {
    this.items.forEach((item, i) => item.setAttribute('aria-selected', i === index ? 'true' : 'false'));
    this.active = index;
    this.items[index].scrollIntoView({ block: 'nearest' });
  }

  open() {
    this.panel.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.controller?.abort();
    this.panel.classList.remove('is-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.items.forEach((item) => item.setAttribute('aria-selected', 'false'));
    this.active = -1;
  }
}

document.querySelectorAll('.gearfull-inline-search, .gearfull-mobile-search').forEach((gearfullHeaderSearch) => {
  new GearfullHeaderSearch(gearfullHeaderSearch);
});
